import {makeAutoObservable, runInAction} from 'mobx'
import {ObjUtil} from 'yougile-util'
import {DataObjectId, GenericStickerState, Hub} from 'yougile-model'
import {MovementClient} from '@client/movement-client'
import {Sync} from '@client/sync'
import {boardSettingsStore} from '@components/board/board-settings/board-settings-store'
import {
  OptionsItem,
  OptionsItemCheckbox
} from '@components/common/base/dropdown-button-menu/types'
import {createUseStore} from '@containers/hooks'
import {createNewMovementTemplate} from './create-new-movement-template'
import {DestinationRet} from './destination/types'
import {getInitColumns} from './helpers/get-init-columns'
import {getInitDestination} from './helpers/get-init-destination'
import {getPinInitStickers} from './helpers/get-pin-init-stickers'
import {getUnpinInitStickers} from './helpers/get-unpin-init-stickers'
import {refreshAfterMovementChanges} from './refresh-after-movement-changes'
import {RemoveAssignedOptions, SelectedPinStickers} from './types'

interface State {
  rows: MovementRow[]
  page: 'main' | 'create' | 'edit'
  editId?: DataObjectId

  // fields for edit tmpl
  title: string
  destination?: DestinationRet
  columnsFrom: OptionsItemCheckbox[]

  removeAssigned?: boolean
  removeAssignedItem?: RemoveAssignedOptions
  removeAssignedUsers: DataObjectId[]
  addNewAssigned?: boolean
  addNewAssignedUsers: DataObjectId[]

  unsubscribe?: boolean
  unsubscribeItem?: RemoveAssignedOptions
  unsubscribeUsers: DataObjectId[]
  subscribe?: boolean
  subscribeUsers: DataObjectId[]

  unpin?: boolean
  unpinStickers: OptionsItemCheckbox[]
  pin?: boolean
  pinStickers: SelectedPinStickers

  errorHasChanges?: boolean
  errorHasChangesBgRed?: boolean
}

export interface MovementRow {
  id: DataObjectId
  enabled: boolean
  text: string
  destinationProject: string
  destinationBoard: string
  destinationColumn: string
}
const getInitValue = (): State => ({
  rows: [],
  page: 'main',
  title: '',
  columnsFrom: [],
  removeAssignedUsers: [],
  addNewAssignedUsers: [],
  unsubscribeUsers: [],
  subscribeUsers: [],
  unpinStickers: [],
  pinStickers: {}
})

export class MovementTemplatesStore {
  state: State = getInitValue()
  companyId: DataObjectId
  boardId: DataObjectId
  editHub?: Hub

  constructor(companyId: DataObjectId, boardId: DataObjectId) {
    makeAutoObservable(this, {}, {autoBind: true})
    this.companyId = companyId
    this.boardId = boardId
  }

  public cancel(): boolean {
    if (this.canSubmit(this.editHub)) {
      this.showError()
      return true
    }

    this.goToMain()
    return true
  }

  public cancelForce(): boolean {
    this.goToMain()
    return true
  }

  public goToMain(): void {
    this.reset()
    this.state.page = 'main'
  }

  public reset(): void {
    this.state = getInitValue()
    this.editHub = undefined

    runInAction(() => {
      boardSettingsStore.setHasChanges(false)
      boardSettingsStore.setOnHasChangesCb(undefined)
    })
  }

  public goToCreate(): void {
    this.state.page = 'create'
  }
  public goToEdit(id: DataObjectId): void {
    this.state.page = 'edit'
    this.state.editId = id
  }

  public setTitle(value: string): void {
    this.state.title = value
  }

  public setDestination(value: DestinationRet): void {
    this.state.destination = value
    this.state.addNewAssignedUsers = []
    this.state.subscribeUsers = []
    this.state.pinStickers = {}
  }

  public get selectedColumnsFromIds(): DataObjectId[] {
    return this.state.columnsFrom
      .filter(({selected}) => selected)
      .map(({id}) => id)
  }
  public setColumnsFrom(value: OptionsItemCheckbox[]): void {
    this.state.columnsFrom = value
  }

  public get removeAssignedItemKey(): RemoveAssignedOptions | undefined {
    const state = this.state
    if (!state.removeAssigned) {
      return undefined
    }
    return state.removeAssignedItem ? state.removeAssignedItem : 'all-current'
  }
  public removeAssignedChanged(value: boolean): void {
    this.state.removeAssigned = value
  }
  public removeAssignedItemChanged(value: OptionsItem): void {
    this.state.removeAssignedItem = value.key as RemoveAssignedOptions
  }
  public removeAssignedUsersChanged(value: DataObjectId[]): void {
    this.state.removeAssignedUsers = value
  }

  public get addNewAssigned(): DataObjectId[] {
    const state = this.state
    return state.addNewAssigned ? state.addNewAssignedUsers : []
  }
  public addNewAssignedChanged(value: boolean): void {
    this.state.addNewAssigned = value
  }
  public addNewAssignedUsersChanged(value: DataObjectId[]): void {
    this.state.addNewAssignedUsers = value
  }

  public get unsubscribeItemKey(): RemoveAssignedOptions | undefined {
    const state = this.state
    if (!state.unsubscribe) {
      return undefined
    }
    return state.unsubscribeItem ? state.unsubscribeItem : 'all-current'
  }
  public unsubscribeChanged(value: boolean): void {
    this.state.unsubscribe = value
  }
  public unsubscribeItemChanged(value: OptionsItem): void {
    this.state.unsubscribeItem = value.key as RemoveAssignedOptions
  }
  public unsubscribeUsersChanged(value: DataObjectId[]): void {
    this.state.unsubscribeUsers = value
  }

  public get subscribe(): DataObjectId[] {
    const state = this.state
    return state.subscribe ? state.subscribeUsers : []
  }
  public subscribeChanged(value: boolean): void {
    this.state.subscribe = value
  }
  public subscribeUsersChanged(value: DataObjectId[]): void {
    this.state.subscribeUsers = value
  }

  public get unpinStickers(): OptionsItemCheckbox[] {
    const state = this.state
    return state.unpin ? state.unpinStickers : []
  }
  public get unpinStickersIds(): DataObjectId[] {
    return this.unpinStickers.filter(({selected}) => selected).map(({id}) => id)
  }
  public unpinChanged(value: boolean): void {
    this.state.unpin = value
  }
  public unpinStickersChanged(value: OptionsItemCheckbox[]): void {
    this.state.unpinStickers = value
  }

  public get pinStickers(): SelectedPinStickers {
    const state = this.state
    return state.pin ? state.pinStickers : {}
  }
  public pinChanged(value: boolean): void {
    this.state.pin = value
  }
  public pinStickersChanged(value: SelectedPinStickers): void {
    this.state.pinStickers = value
  }

  pinSticker(stickerId: DataObjectId, value: GenericStickerState): void {
    const prevValue = this.state.pinStickers
    this.state.pinStickers = {
      ...prevValue,
      [stickerId]: value
    }
  }

  unpinSticker(stickerId: DataObjectId): void {
    delete this.state.pinStickers[stickerId]
  }

  public initState(editHub?: Hub): void {
    const box = Sync.getCurrentSandbox()
    const board = box.getHub(this.boardId)
    this.editHub = editHub

    if (!editHub) {
      this.initCreateState(board)
    } else {
      this.initEditState(board, editHub)
    }
  }

  private initCreateState(board: Hub): void {
    this.state.columnsFrom = getInitColumns(this.boardId)

    this.state.unpinStickers = getUnpinInitStickers(board)
    this.state.pinStickers = getPinInitStickers()
  }

  private initEditState(board: Hub, editHub: Hub): void {
    const tmpl = editHub.data.movementTemplate
    if (!tmpl) {
      return
    }

    this.state.title = tmpl?.title || ''
    this.state.destination = getInitDestination(editHub)
    this.state.columnsFrom = getInitColumns(this.boardId, editHub)

    this.state.removeAssigned = !!tmpl.removeAssignedItem
    this.state.removeAssignedItem = tmpl.removeAssignedItem
    this.state.removeAssignedUsers = tmpl.removeAssignedUsers
    this.state.addNewAssigned = !!tmpl.addNewAssigned.length
    this.state.addNewAssignedUsers = tmpl.addNewAssigned

    this.state.unsubscribe = !!tmpl.unsubscribeItem
    this.state.unsubscribeItem = tmpl.unsubscribeItem
    this.state.unsubscribeUsers = tmpl.unsubscribeUsers
    this.state.subscribe = !!tmpl.subscribeUsers.length
    this.state.subscribeUsers = tmpl.subscribeUsers

    this.state.unpin = !!tmpl.unpinStickers?.length
    this.state.unpinStickers = getUnpinInitStickers(board, editHub)

    this.state.pin = !!Object.keys(tmpl.pinStickers || {}).length
    this.state.pinStickers = tmpl.pinStickers as SelectedPinStickers
  }

  public save(editHub: Hub): void {
    if (!this.canSubmit(editHub)) {
      return
    }

    if (editHub) {
      this.saveEditHub(editHub)
    } else {
      this.createTemplate()
    }

    this.goToMain()
  }

  private saveEditHub(editHub: Hub): void {
    const state = this.state
    if (!editHub) {
      return
    }

    const tmpl = editHub.data.movementTemplate
    if (!tmpl) {
      return
    }
    const dest = state.destination
    if (!dest?.project || !dest?.board || !dest?.column) {
      return
    }

    tmpl.title = state.title
    tmpl.destinationProjectId = dest.project.id
    tmpl.destinationBoardId = dest.board.id
    tmpl.destinationColumnId = dest.column.id
    tmpl.moveFromColumns = this.selectedColumnsFromIds
    tmpl.moveFromColumnsAllIn =
      state.columnsFrom.length === this.selectedColumnsFromIds.length

    tmpl.removeAssignedItem = this.removeAssignedItemKey
    tmpl.removeAssignedUsers = state.removeAssignedUsers
    tmpl.addNewAssigned = this.addNewAssigned

    tmpl.unsubscribeItem = this.unsubscribeItemKey
    tmpl.unsubscribeUsers = state.unsubscribeUsers
    tmpl.subscribeUsers = this.subscribe

    tmpl.unpinStickers = this.unpinStickersIds
    tmpl.pinStickers = this.pinStickers

    editHub.commit()

    refreshAfterMovementChanges(editHub.data.movementTemplate.boardId)
  }

  private createTemplate(): void {
    const state = this.state
    const allIn =
      state.columnsFrom.length === this.selectedColumnsFromIds.length

    createNewMovementTemplate({
      boardId: this.boardId,
      title: state.title,

      destinationProjectId: state.destination.project.id,
      destinationBoardId: state.destination.board.id,
      destinationColumnId: state.destination.column.id,
      moveFromColumns: this.selectedColumnsFromIds,
      moveFromColumnsAllIn: allIn,

      removeAssignedItem: this.removeAssignedItemKey,
      removeAssignedUsers: state.removeAssignedUsers,
      addNewAssigned: this.addNewAssigned,

      unsubscribeItem: this.unsubscribeItemKey,
      unsubscribeUsers: state.unsubscribeUsers,
      subscribeUsers: this.subscribe,

      unpinStickers: this.unpinStickersIds,
      pinStickers: this.pinStickers
    })
  }

  public canSubmit(editHub?: Hub): boolean {
    const validToCreate = this.validToCreate()
    if (!editHub) {
      runInAction(() => {
        boardSettingsStore.setHasChanges(validToCreate)
        const cb = validToCreate ? this.showError : undefined
        boardSettingsStore.setOnHasChangesCb(cb)
      })

      return validToCreate
    }

    const hasChanges = validToCreate && this.isTemplateChanged(editHub)

    runInAction(() => {
      boardSettingsStore.setHasChanges(hasChanges)

      const cb = hasChanges ? this.showError : undefined
      boardSettingsStore.setOnHasChangesCb(cb)
    })

    return hasChanges
  }
  public validToCreate(): boolean {
    const state = this.state
    const {title, destination} = state
    const selectedColumns = this.selectedColumnsFromIds

    const validTitle = title.trim().length >= 1
    const validDestination =
      destination?.project?.id &&
      destination?.board?.id &&
      destination?.column?.id
    const validSelectedColumns = !!selectedColumns.length

    return validTitle && validDestination && validSelectedColumns
  }

  public isTemplateChanged(editHub: Hub): boolean {
    const tmpl = editHub.data.movementTemplate
    const {title, destination} = this.state
    const selectedColumns = this.selectedColumnsFromIds
    const removeAssignedItem = this.removeAssignedItemKey
    const removeAssignedUsers = this.state.removeAssignedUsers
    const addNewAssigned = this.addNewAssigned

    const unsubscribeItem = this.unsubscribeItemKey
    const unsubscribeUsers = this.state.unsubscribeUsers
    const subscribe = this.subscribe

    const unpinStickersIds = this.unpinStickersIds

    const titleEquals = tmpl.title === title
    if (!titleEquals) {
      return true
    }

    const destProjEq = destination?.project?.id === tmpl.destinationProjectId
    const destBoardEq = destination?.board?.id === tmpl.destinationBoardId
    const destColumnEq = destination?.column?.id === tmpl.destinationColumnId
    if (!(destProjEq && destBoardEq && destColumnEq)) {
      return true
    }

    const colFromEquals =
      selectedColumns.length === tmpl.moveFromColumns.length &&
      selectedColumns.every(id => tmpl.moveFromColumns.includes(id))
    if (!colFromEquals) {
      return true
    }

    const removeAssignedKeyEq = removeAssignedItem === tmpl.removeAssignedItem
    if (!removeAssignedKeyEq) {
      return true
    }

    if (removeAssignedItem === 'specific-users') {
      const removeAssignedUsersEq =
        removeAssignedUsers.length === tmpl.removeAssignedUsers.length &&
        removeAssignedUsers.every(id => tmpl.removeAssignedUsers.includes(id))
      if (!removeAssignedUsersEq) {
        return true
      }
    }

    const addNewAssignedEq =
      addNewAssigned.length === tmpl.addNewAssigned.length &&
      addNewAssigned.every(id => tmpl.addNewAssigned.includes(id))
    if (!addNewAssignedEq) {
      return true
    }

    const unsubscribeKeyEq = unsubscribeItem === tmpl.unsubscribeItem
    if (!unsubscribeKeyEq) {
      return true
    }

    if (unsubscribeItem === 'specific-users') {
      const unsubscribeUsersEq =
        unsubscribeUsers.length === tmpl.unsubscribeUsers.length &&
        unsubscribeUsers.every(id => tmpl.unsubscribeUsers.includes(id))
      if (!unsubscribeUsersEq) {
        return true
      }
    }

    const subscribeEq =
      subscribe.length === tmpl.subscribeUsers.length &&
      subscribe.every(id => tmpl.subscribeUsers.includes(id))
    if (!subscribeEq) {
      return true
    }

    const unpinEquals =
      unpinStickersIds.length === tmpl.unpinStickers.length &&
      unpinStickersIds.every(id => tmpl.unpinStickers.includes(id))
    if (!unpinEquals) {
      return true
    }

    const pinEquals = ObjUtil.deepEqual(
      this.pinStickers,
      tmpl.pinStickers,
      true
    )
    if (!pinEquals) {
      return true
    }

    return false
  }

  public showError(): void {
    this.state.errorHasChanges = true
    this.state.errorHasChangesBgRed = true
    setTimeout(() => {
      runInAction(() => {
        this.state.errorHasChangesBgRed = false
      })
    }, 300)
  }

  public syncRows(): void {
    const box = Sync.getCurrentSandbox()
    const list = MovementClient.listTemplatesInBoard(this.boardId)

    this.state.rows = list.map(l => {
      const {id, title, enabled} = l.data.movementTemplate
      const {destinationProjectId, destinationBoardId, destinationColumnId} =
        l.data.movementTemplate

      const destinationProject = box.getHub(destinationProjectId)?.title || ''
      const destinationBoard = box.getHub(destinationBoardId)?.title || ''
      const destinationColumn = box.getHub(destinationColumnId)?.title || ''

      return {
        id,
        enabled,
        text: title,
        destinationProject,
        destinationBoard,
        destinationColumn
      }
    })
  }
}

const [MovementContext, useMovementStore] = createUseStore(
  MovementTemplatesStore
)
export {MovementContext, useMovementStore}
